<template>
  <div class="">
    <!-- 打单余额在线充值：信用卡 Stripe / 微信 / 支付宝 / PayPal，充到打单余额 -->
    <el-card class="box-card recharge-card">
      <!-- 当前余额 -->
      <div class="balance-row">
        <span class="balance-label">{{ $t('recharge.balanceLabel') }}</span>
        <span class="balance-value">${{ moneyText }}</span>
      </div>

      <el-form label-width="90px" label-position="left" size="small" class="recharge-form">
        <!-- 充值金额 -->
        <el-form-item :label="$t('recharge.amountLabel')">
          <el-input
            v-model="amount"
            :placeholder="$t('recharge.amountPlaceholder')"
            style="width: 260px"
            @input="onAmountInput"
          >
            <template slot="prepend">$</template>
          </el-input>
          <div class="quick-row">
            <el-button
              v-for="q in quickList"
              :key="q"
              size="mini"
              :type="Number(amount) === q ? 'primary' : 'default'"
              @click="amount = String(q)"
            >${{ q }}</el-button>
          </div>
          <div class="amount-tip">{{ $t('recharge.amountTip') }}</div>
        </el-form-item>

        <!-- 支付方式 -->
        <el-form-item :label="$t('recharge.methodLabel')">
          <el-radio-group v-model="method">
            <el-radio-button label="card">{{ $t('recharge.methodCard') }}</el-radio-button>
            <el-radio-button label="wechatpay">{{ $t('recharge.methodWechat') }}</el-radio-button>
            <el-radio-button label="alipay">{{ $t('recharge.methodAlipay') }}</el-radio-button>
            <el-radio-button label="paypal">{{ $t('recharge.methodPaypal') }}</el-radio-button>
          </el-radio-group>
        </el-form-item>

        <el-form-item>
          <el-button type="primary" icon="el-icon-wallet" @click="onConfirm">
            {{ $t('recharge.confirmBtn') }}{{ payAmountText }}
          </el-button>
        </el-form-item>
      </el-form>
    </el-card>

    <!-- 支付弹窗：按支付方式渲染对应支付区 -->
    <el-dialog
      :title="$t('recharge.payTitle')"
      :close-on-click-modal="false"
      :visible.sync="payVisible"
      width="420px"
      append-to-body
      @opened="onDialogOpened"
      @closed="onDialogClosed"
    >
      <div class="pay-body" v-loading="loadingPay" :element-loading-text="$t('recharge.creatingOrder')">
        <div class="pay-amount">${{ payAmount.toFixed(2) }}</div>

        <!-- 信用卡 Stripe -->
        <div v-show="method === 'card'">
          <div id="recharge-stripe-el" class="stripe-el"></div>
          <el-button
            v-show="!loadingPay"
            type="primary"
            style="width: 100%; margin-top: 14px"
            :loading="paying"
            @click="doStripePay"
          >{{ paying ? $t('recharge.paying') : $t('recharge.payNow') }}</el-button>
        </div>

        <!-- PayPal -->
        <div v-show="method === 'paypal'">
          <div id="recharge-paypal-btns"></div>
        </div>

        <!-- 微信 / 支付宝二维码 -->
        <div v-show="method === 'wechatpay' || method === 'alipay'" class="qr-wrap">
          <div class="qr-tip">{{ method === 'alipay' ? $t('recharge.scanAlipayTip') : $t('recharge.scanWechatTip') }}</div>
          <div id="recharge-qr" class="qr-box"></div>
          <div class="qr-countdown">{{ countdownText }}</div>
          <el-button v-if="qrExpired" size="small" @click="mountHantepay">{{ $t('recharge.regenerate') }}</el-button>
        </div>

        <div v-if="payMsg" class="pay-msg">{{ payMsg }}</div>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import {
  rechargeStripeCreate,
  rechargeStripeConfirm,
  rechargePaypalConfig,
  rechargePaypalCreate,
  rechargePaypalCapture,
  rechargeHantepayCreate,
  rechargeHantepayQuery,
} from '@/api/recharge';

// 动态加载外部脚本（加载一次，复用）
function loadScript(src, globalName) {
  return new Promise((resolve, reject) => {
    if (globalName && window[globalName]) return resolve(window[globalName]);
    const exist = document.querySelector('script[data-src="' + src + '"]');
    if (exist) {
      if (exist.getAttribute('data-loaded') === '1') return resolve(globalName ? window[globalName] : true);
      exist.addEventListener('load', () => resolve(globalName ? window[globalName] : true));
      exist.addEventListener('error', () => reject(new Error('load fail: ' + src)));
      return;
    }
    const s = document.createElement('script');
    s.src = src;
    s.async = true;
    s.setAttribute('data-src', src);
    s.onload = () => { s.setAttribute('data-loaded', '1'); resolve(globalName ? window[globalName] : true); };
    s.onerror = () => reject(new Error('load fail: ' + src));
    document.head.appendChild(s);
  });
}

const EXPIRE = 300; // 二维码有效期(秒)
const INTERVAL = 3000; // 轮询间隔(毫秒)

export default {
  data() {
    return {
      amount: '',
      method: 'card',
      quickList: [50, 100, 200, 500, 1000],
      payVisible: false,
      loadingPay: false,
      paying: false,
      payMsg: '',
      payAmount: 0,
      rechargeSn: '',
      hantepaySns: [],
      pollIndex: 0,
      pollBusy: false,
      creatingQr: false,
      paymentVersion: 0,
      paymentDone: false,
      // Stripe
      stripe: null,
      elements: null,
      // 二维码
      qrExpired: false,
      remaining: 0,
      pollTimer: null,
      cdTimer: null,
    };
  },
  computed: {
    moneyText() {
      const m = this.$store.state.money;
      return m ? Number(m).toFixed(2) : '0.00';
    },
    payAmountText() {
      const n = Number(this.amount);
      return n > 0 ? ' $' + n.toFixed(2) : '';
    },
    countdownText() {
      if (this.qrExpired) return this.$t('recharge.qrExpired');
      if (this.remaining <= 0) return '';
      const m = Math.floor(this.remaining / 60);
      const x = this.remaining % 60;
      return this.$t('recharge.qrLeft') + ' ' + (m < 10 ? '0' : '') + m + ':' + (x < 10 ? '0' : '') + x;
    },
  },
  watch: {
    payVisible(value) {
      if (!value) {
        this.paymentVersion++;
        this.stopTimers();
      }
    },
  },
  methods: {
    // 只允许数字与小数点
    onAmountInput(v) {
      this.amount = String(v).replace(/[^\d.]/g, '');
    },
    // 校验金额（美元）：返回 number 或 null
    parseAmount() {
      const raw = (this.amount || '').trim();
      if (!raw || isNaN(Number(raw))) {
        this.$message.warning(this.$t('recharge.amountInvalid'));
        return null;
      }
      if (/\.\d{3,}$/.test(raw)) {
        this.$message.warning(this.$t('recharge.amountInvalid'));
        return null;
      }
      const n = Math.round(Number(raw) * 100) / 100;
      if (n < 1) { this.$message.warning(this.$t('recharge.amountMin')); return null; }
      if (n > 50000) { this.$message.warning(this.$t('recharge.amountMax')); return null; }
      return n;
    },
    // 点确认充值：校验 → 打开支付弹窗
    onConfirm() {
      const amt = this.parseAmount();
      if (amt == null) return;
      if (this.payVisible) return;
      this.paymentVersion++;
      this.paymentDone = false;
      this.hantepaySns = [];
      this.rechargeSn = '';
      this.pollIndex = 0;
      this.pollBusy = false;
      this.creatingQr = false;
      this.payAmount = amt;
      this.payMsg = '';
      // 打开弹窗前先置 loading：弹窗内容在 payVisible=true 时立即渲染，而真正加载支付组件要等到
      // el-dialog 动画结束后的 onDialogOpened，中间这段时间若 loadingPay 为 false，
      // “立即支付”按钮(v-show="!loadingPay")会闪现一下，故提前置 true。
      this.loadingPay = true;
      this.payVisible = true;
    },
    // 弹窗完全打开（DOM 就绪）后按方式初始化支付
    onDialogOpened() {
      if (this.method === 'card') this.mountStripe();
      else if (this.method === 'paypal') this.mountPaypal();
      else this.mountHantepay();
    },
    // 弹窗关闭：清理定时器与 Stripe 实例
    onDialogClosed() {
      this.stopTimers();
      this.stripe = null;
      this.elements = null;
      this.paying = false;
      this.qrExpired = false;
      const box = document.getElementById('recharge-qr');
      if (box) box.innerHTML = '';
      const pp = document.getElementById('recharge-paypal-btns');
      if (pp) pp.innerHTML = '';
    },
    // 充值成功统一处理：刷新余额 + 提示 + 关闭弹窗
    onPaySuccess(money) {
      if (!this.payVisible || this.paymentDone) return;
      this.paymentDone = true;
      this.stopTimers();
      if (money !== undefined && money !== null && money !== '') {
        this.$store.dispatch('updateMoney', money);
      }
      this.payVisible = false;
      this.$message.success(this.$t('recharge.paySuccess'));
    },

    // ===== 信用卡 Stripe =====
    async mountStripe() {
      this.payMsg = '';
      this.loadingPay = true;
      try {
        const res = await rechargeStripeCreate(this.payAmount);
        if (!res || res.code != 1 || !res.data || !res.data.client_secret) {
          this.loadingPay = false;
          this.payMsg = (res && res.msg) || this.$t('recharge.netErr');
          return;
        }
        this.rechargeSn = res.data.recharge_sn;
        const StripeCtor = await loadScript('https://js.stripe.com/v3/', 'Stripe');
        this.stripe = StripeCtor(res.data.publishable_key);
        this.elements = this.stripe.elements({
          clientSecret: res.data.client_secret,
          locale: window.localStorage.getItem('lhkdLang') === 'en' ? 'en' : 'zh',
        });
        const pe = this.elements.create('payment');
        // Payment Element 真正渲染完成(ready)才结束 loading 并显示“立即支付”。
        // mount() 只是挂载，输入框是异步渲染的（图中灰色骨架屏即渲染中），
        // 若 mount 后立即结束 loading，会出现骨架屏还在、按钮已显示的问题。
        pe.on('ready', () => {
          this.loadingPay = false;
        });
        pe.mount('#recharge-stripe-el');
      } catch (e) {
        this.loadingPay = false;
        this.payMsg = this.$t('recharge.netErr');
      }
    },
    async doStripePay() {
      if (this.paying || !this.stripe || !this.elements) return;
      this.payMsg = '';
      this.paying = true;
      try {
        const result = await this.stripe.confirmPayment({ elements: this.elements, redirect: 'if_required' });
        if (result.error) {
          this.paying = false;
          this.payMsg = result.error.message || this.$t('recharge.payFail');
          return;
        }
        const pi = result.paymentIntent;
        if (pi && (pi.status === 'succeeded' || pi.status === 'processing')) {
          const r = await rechargeStripeConfirm(this.rechargeSn);
          if (r && r.code == 1 && r.data) {
            this.onPaySuccess(r.data.money);
          } else {
            this.paying = false;
            this.payMsg = (r && r.msg) || this.$t('recharge.payFail');
          }
        } else {
          this.paying = false;
          this.payMsg = this.$t('recharge.payFail');
        }
      } catch (e) {
        this.paying = false;
        this.payMsg = this.$t('recharge.netErr');
      }
    },

    // ===== PayPal =====
    async mountPaypal() {
      this.payMsg = '';
      this.loadingPay = true;
      try {
        const cfg = await rechargePaypalConfig();
        if (!cfg || cfg.code != 1 || !cfg.data || !cfg.data.client_id) {
          this.loadingPay = false;
          this.payMsg = (cfg && cfg.msg) || this.$t('recharge.netErr');
          return;
        }
        const url = 'https://www.paypal.com/sdk/js?client-id=' + encodeURIComponent(cfg.data.client_id)
          + '&currency=' + encodeURIComponent(cfg.data.currency || 'USD') + '&intent=capture&components=buttons';
        const paypal = await loadScript(url, 'paypal');
        this.loadingPay = false;
        const holder = document.getElementById('recharge-paypal-btns');
        if (holder) holder.innerHTML = '';
        const self = this;
        paypal.Buttons({
          style: { layout: 'vertical', height: 44, shape: 'rect', label: 'paypal' },
          createOrder() {
            return rechargePaypalCreate(self.payAmount).then((r) => {
              if (r && r.code == 1 && r.data && r.data.order_id) {
                self.rechargeSn = r.data.recharge_sn;
                return r.data.order_id;
              }
              throw new Error((r && r.msg) || self.$t('recharge.payFail'));
            });
          },
          onApprove(data) {
            return rechargePaypalCapture(self.rechargeSn, data.orderID).then((r) => {
              if (r && r.code == 1 && r.data) self.onPaySuccess(r.data.money);
              else throw new Error((r && r.msg) || self.$t('recharge.payFail'));
            });
          },
          onError(err) {
            self.payMsg = (err && err.message) || self.$t('recharge.payFail');
          },
        }).render('#recharge-paypal-btns');
      } catch (e) {
        this.loadingPay = false;
        this.payMsg = this.$t('recharge.netErr');
      }
    },

    // ===== 微信 / 支付宝（HantePay 扫码） =====
    async mountHantepay() {
      if (this.creatingQr || !this.payVisible || this.paymentDone) return;
      const version = this.paymentVersion;
      this.creatingQr = true;
      this.stopTimers();
      this.loadingPay = true;
      this.payMsg = '';
      try {
        if (this.rechargeSn) {
          await this.checkHantepay(this.rechargeSn, version);
          if (version !== this.paymentVersion || !this.payVisible || this.paymentDone) return;
        }
        const res = await rechargeHantepayCreate(this.payAmount, this.method);
        if (version !== this.paymentVersion || !this.payVisible || this.paymentDone) return;
        if (!res || res.code != 1 || !res.data || !res.data.code_url) throw new Error((res && res.msg) || this.$t('recharge.netErr'));
        this.rechargeSn = res.data.recharge_sn;
        this.hantepaySns.push(this.rechargeSn);
        const QRCode = await loadScript('https://www.ecpai.com/js/qrcode.min.js', 'QRCode');
        if (version !== this.paymentVersion || !this.payVisible || this.paymentDone) return;
        const el = document.getElementById('recharge-qr');
        if (el) {
          el.innerHTML = '';
          try {
            new QRCode(el, { text: res.data.code_url, width: 200, height: 200, correctLevel: QRCode.CorrectLevel.M });
          } catch (e) {
            el.innerHTML = '<a href="' + res.data.code_url + '" target="_blank" rel="noopener noreferrer">' + res.data.code_url + '</a>';
          }
        }
        this.qrExpired = false;
        this.startCountdown();
      } catch (e) {
        if (version === this.paymentVersion && this.payVisible && !this.paymentDone) this.payMsg = e.message || this.$t('recharge.netErr');
      } finally {
        if (version === this.paymentVersion) {
          this.creatingQr = false;
          this.loadingPay = false;
          if (this.payVisible && !this.paymentDone && this.hantepaySns.length) this.startPolling();
        }
      }
    },
    startCountdown() {
      this.remaining = EXPIRE;
      this.cdTimer = setInterval(() => {
        this.remaining--;
        if (this.remaining <= 0) {
          // 倒计时只控制二维码提示，支付确认继续，避免漏掉延迟付款。
          clearInterval(this.cdTimer);
          this.cdTimer = null;
          this.qrExpired = true;
        }
      }, 1000);
    },
    async checkHantepay(sn, version) {
      const res = await rechargeHantepayQuery(sn);
      if (version !== this.paymentVersion || !this.payVisible || this.paymentDone) return;
      if (!res || res.code != 1 || !res.data) throw new Error((res && res.msg) || this.$t('recharge.netErr'));
      if (res.data.status === 'paid') this.onPaySuccess(res.data.money);
    },
    startPolling() {
      if (this.pollTimer) clearInterval(this.pollTimer);
      this.pollTimer = setInterval(async () => {
        if (this.pollBusy || !this.payVisible || this.paymentDone || !this.hantepaySns.length) return;
        const version = this.paymentVersion;
        const sn = this.hantepaySns[this.pollIndex++ % this.hantepaySns.length];
        this.pollBusy = true;
        try {
          await this.checkHantepay(sn, version);
        } catch (e) {
          if (version === this.paymentVersion && this.payVisible && !this.paymentDone) this.payMsg = e.message || this.$t('recharge.netErr');
        } finally {
          if (version === this.paymentVersion) this.pollBusy = false;
        }
      }, INTERVAL);
    },
    stopTimers() {
      if (this.pollTimer) { clearInterval(this.pollTimer); this.pollTimer = null; }
      if (this.cdTimer) { clearInterval(this.cdTimer); this.cdTimer = null; }
    },
  },
  beforeDestroy() {
    this.paymentVersion++;
    this.stopTimers();
  },
};
</script>

<style lang="scss" scoped>
.recharge-card {
  text-align: left;
}
.balance-row {
  display: flex;
  align-items: baseline;
  margin-bottom: 20px;
  padding: 16px 20px;
  background: linear-gradient(135deg, #4d80f0, #3563d6);
  border-radius: 8px;
  color: #fff;
}
.balance-label {
  font-size: 14px;
  opacity: 0.9;
  margin-right: 12px;
}
.balance-value {
  font-size: 28px;
  font-weight: 700;
}
.quick-row {
  margin-top: 10px;
  .el-button {
    margin-right: 8px;
    margin-bottom: 6px;
  }
}
.amount-tip {
  margin-top: 6px;
  font-size: 12px;
  color: #909399;
}
.pay-body {
  min-height: 120px;
}
.pay-amount {
  text-align: center;
  font-size: 26px;
  font-weight: 700;
  color: #3563d6;
  margin-bottom: 16px;
}
.stripe-el {
  min-height: 40px;
}
.qr-wrap {
  text-align: center;
}
.qr-tip {
  font-size: 13px;
  color: #606266;
  margin-bottom: 12px;
}
.qr-box {
  display: inline-block;
  min-height: 200px;
}
.qr-countdown {
  margin-top: 10px;
  font-size: 12px;
  color: #909399;
}
.pay-msg {
  margin-top: 12px;
  font-size: 13px;
  color: #f56c6c;
  text-align: center;
  word-break: break-all;
}
</style>
